// @flow

import * as React from "react"
import ReactMapGL, { Marker } from "react-map-gl"
import { compose } from "react-apollo"
import { FormattedMessage } from "react-intl"
import { connect } from "react-redux"
import { TweenMax, Cubic } from "gsap"
import { setModal } from "../../Modal"
import { SilentErrorBoundary } from "../../UI/Errors"
import { MAPBOX_ACCESS_TOKEN } from "../../API"
import { Legend, AqiLevel, AqiDescription, levels } from "../Shared/AQILegend"
import LocationsContainer from "./LocationsContainer"
import MapOverlay from "./MapOverlay"
import LocationReport from "./LocationReport"
import LocationList from "./LocationList"
import LocationMarker from "./LocationMarker"
import LocationName from "./LocationName"
import LocationMetrics from "./LocationMetrics"
import { LocationBars } from "./LocationBars"
import { ForecastMetricTitleGroup } from "../CurrentAirQualityView"
import { withCurrentKioskData } from "../queries"

import Location from "./Location"
import Divider from "./Divider"

import type { CurrentKiosk, PublicKiosk } from "../queries"
import type { Match, RouterHistory } from "react-router-dom"
import type { SetModal } from "../../Modal"
import type { LocationReading } from "./LocationBars"

import {
  Container,
  RightAlignedModal,
  Content,
  LocationsBody,
  Header,
  PrimaryHeading,
  SecondaryTitleHeading,
  PrimaryButton,
  ButtonGroup,
  Fade
} from "../Modal"

type Props = {
  match: Match,
  history: RouterHistory,
  setModal: SetModal,
  data: CurrentKiosk
}

type LiveReading = { o3: number, pm2_5: number, pm10: number }

type State = {
  readings: LiveReading[]
}

class LocationsModal extends React.Component<Props, State> {
  body: ?HTMLElement
  fade: ?HTMLElement
  mapContainer: ?HTMLElement
  legendKeys: Array<HTMLElement>
  titles: Array<HTMLElement>
  markers: Array<HTMLElement>
  mapMarkers: Array<HTMLElement>
  locationNames: Array<HTMLElement>
  metricTitles: Array<HTMLElement>

  constructor(props) {
    super(props)
    this.body = null
    this.fade = null
    this.mapContainer = null
    this.legendKeys = []
    this.titles = []
    this.markers = []
    this.mapMarkers = []
    this.locationNames = []
    this.metricTitles = []
    this.state = { readings: this.mapLocationData() }
  }

  componentDidMount = () => {
    TweenMax.set(this.body, { y: "-=50", opacity: 0 })
    TweenMax.to(this.body, 0.5, { ease: Cubic.easeOut, opacity: 1, y: 0 })

    TweenMax.set(this.fade, { opacity: 0 })
    TweenMax.to(this.fade, 0.5, { ease: Cubic.easeOut, opacity: 1 })

    TweenMax.set(this.mapContainer, { opacity: 0, scale: 1.25 })
    TweenMax.to(this.mapContainer, 0.75, {
      ease: Cubic.easeOut,
      opacity: 1,
      scale: 1,
      delay: 2
    })

    TweenMax.set(this.legendKeys, { opacity: 0, y: "+=50" })
    TweenMax.staggerTo(
      this.legendKeys,
      0.5,
      { ease: Cubic.easeOut, opacity: 1, y: 0, delay: 1 },
      0.25
    )

    TweenMax.set(this.titles, { opacity: 0, y: "+=30" })
    TweenMax.staggerTo(
      this.titles,
      0.5,
      { ease: Cubic.easeOut, opacity: 1, y: 0, delay: 0.25 },
      0.25
    )

    TweenMax.set(this.metricTitles, { opacity: 0, x: "+=30" })
    TweenMax.staggerTo(
      this.metricTitles,
      0.35,
      { ease: Cubic.easeOut, opacity: 1, x: 0, delay: 1.25 },
      0.25
    )

    TweenMax.set(this.locationNames, { opacity: 0, x: "+=30" })
    TweenMax.staggerTo(
      this.locationNames,
      0.25,
      { ease: Cubic.easeOut, opacity: 1, x: 0, delay: 1.75 },
      0.1
    )

    TweenMax.set(this.markers, { opacity: 0, y: "-=30" })
    TweenMax.staggerTo(
      this.markers,
      0.25,
      { ease: Cubic.easeOut, opacity: 1, y: 0, delay: 1.5 },
      0.1
    )

    TweenMax.set(this.mapMarkers, { opacity: 0, y: "-=30" })
    TweenMax.staggerTo(
      this.mapMarkers,
      0.35,
      { ease: Cubic.easeOut, opacity: 1, y: 0, delay: 3 },
      0.25
    )
  }

  handleClose = () => {
    this.dismiss()
  }

  dismiss = () => {
    const { history, setModal } = this.props
    setModal(false)
    TweenMax.to(this.body, 0.5, { ease: Cubic.easeOut, opacity: 0, y: "+=50" })
    TweenMax.to(this.fade, 0.5, {
      ease: Cubic.easeOut,
      opacity: 0,
      onComplete: () => {
        history.goBack()
      }
    })
  }

  componentDidUpdate(prevProps) {
    const loading = this.props.data.loading
    if (!loading && loading !== prevProps.data.loading) {
      this.setState(() => ({ readings: this.mapLocationData() }))
    }
  }

  mapLocationData = (): LiveReading[] => {
    const publicKiosks = this.props.data.publicKiosks
    let readings = []
    if (publicKiosks) {
      publicKiosks.map(kiosk =>
        readings.push({
          o3: kiosk.readings.live
            .map(data => data.o3)
            .filter(data => data > -1)[0],
          pm2_5: kiosk.readings.live
            .map(data => data.pm2_5)
            .filter(data => data > -1)[0],
          pm10: kiosk.readings.live
            .map(data => data.pm10)
            .filter(data => data > -1)[0]
        })
      )
    }
    return readings
  }

  render() {
    const { data: { publicKiosks } } = this.props
    const { readings } = this.state
    const kiosks: PublicKiosk[] = publicKiosks || []

    return (
      <Container>
        <Fade onClick={this.handleClose} dark innerRef={el => (this.fade = el)}>
          <div
            style={{ position: "absolute", top: 0, left: 0 }}
            ref={el => (this.mapContainer = el)}
          >
            <SilentErrorBoundary>
              <ReactMapGL
                mapboxApiAccessToken={MAPBOX_ACCESS_TOKEN}
                width={1920}
                height={980}
                style={{ position: "absolute", top: 0, left: 0 }}
                latitude={33.533793}
                longitude={-111.402054}
                attributionControl={false}
                zoom={9}
                mapStyle="mapbox://styles/jimjeffers/cj7oze0x6bnqw2rqm6iiztg3s"
              >
                {kiosks.map(
                  ({ primarySensor, latitude, longitude, id }, index) => (
                    <Marker
                      key={`mapMarker_${id}`}
                      latitude={
                        primarySensor.latitude !== 0
                          ? primarySensor.latitude
                          : latitude
                      }
                      longitude={
                        primarySensor.longitude !== 0
                          ? primarySensor.longitude
                          : longitude
                      }
                      offsetTop={-70}
                      offsetLeft={-25}
                    >
                      <LocationMarker
                        labelText={`${index + 1}`}
                        innerRef={el => this.mapMarkers.push(el)}
                      />
                    </Marker>
                  )
                )}
              </ReactMapGL>
            </SilentErrorBoundary>
            <MapOverlay />
          </div>
        </Fade>
        <RightAlignedModal>
          <LocationsBody innerRef={el => (this.body = el)}>
            <Header>
              <PrimaryHeading innerRef={el => this.titles.push(el)}>
                <FormattedMessage id="kiosk.compare.title" />
              </PrimaryHeading>
              <SecondaryTitleHeading innerRef={el => this.titles.push(el)}>
                <FormattedMessage id="kiosk.compare.subtitle" />
              </SecondaryTitleHeading>
            </Header>
            <Content>
              <LocationsContainer>
                <LocationReport innerRef={el => this.titles.push(el)}>
                  <Legend compact={true}>
                    {levels.map(level => (
                      <AqiLevel
                        key={level}
                        level={level}
                        innerRef={el => this.legendKeys.push(el)}
                        compact={true}
                      >
                        <AqiDescription compact={true}>
                          <FormattedMessage
                            id={`kiosk.history.${level
                              .toLowerCase()
                              .replace(" ", "")}`}
                          />
                        </AqiDescription>
                      </AqiLevel>
                    ))}
                  </Legend>
                  <Divider />
                  {["pm2_5", "pm10", "o3"].map((metric, index) => {
                    const readingData: LocationReading[] = readings.map(
                      (values, index) => ({
                        location: index + 1,
                        reading: values ? values[metric] : null,
                        pollutant: metric
                      })
                    )
                    return (
                      <LocationMetrics key={`metric_${index}`}>
                        <LocationBars readings={readingData} delay={1} />
                        <ForecastMetricTitleGroup
                          pollutant={metric}
                          innerRefs={el => this.metricTitles.push(el)}
                        />
                      </LocationMetrics>
                    )
                  })}
                </LocationReport>
                <LocationList innerRef={el => this.titles.push(el)}>
                  {kiosks.map(({ id, friendlyName }, index) => (
                    <Location key={`location_${id}`}>
                      <LocationMarker
                        labelText={`${index + 1}`}
                        innerRef={el => this.markers.push(el)}
                      />
                      <LocationName
                        innerRef={el => this.locationNames.push(el)}
                      >
                        {friendlyName}
                      </LocationName>
                    </Location>
                  ))}
                </LocationList>
              </LocationsContainer>
              <ButtonGroup innerRef={el => this.titles.push(el)}>
                <PrimaryButton onClick={this.handleClose}>
                  <FormattedMessage id="kiosk.history.close" />
                </PrimaryButton>
              </ButtonGroup>
            </Content>
          </LocationsBody>
        </RightAlignedModal>
      </Container>
    )
  }
}

const ModalWithData = compose(withCurrentKioskData)(LocationsModal)

export default connect(null, {
  setModal
})(ModalWithData)



// WEBPACK FOOTER //
// ./src/Kiosk/LocationsModal/LocationsModal.js